'use client'

import { motion } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import FullBleedSection from '@/components/FullBleedSection'
import LazyBackgroundVideo from '@/components/LazyBackgroundVideo'
import AIOptimizedSchema from '@/components/AIOptimizedSchema'

// --- AI-optimized schema data for Isolierung service ---
const isolierungSchemaData = {
  name: 'Volks-Isolierung',
  description:
    'Bis zu 50% weniger Energiekosten durch Low Emission Coating. Innovative Isolierung mit verbessertem Raumklima zu einem günstigen Preis.',
  url: 'https://godeutschland.de/isolierung',
  price: '90',
  category: 'Energieeffizienz',
  features: [
    'Low Emission Coating',
    'Bis zu 50% Energiekosteneinsparung',
    'Verbessertes Raumklima',
    'Kostengünstige Anbringung',
    'Innovative Beschichtungstechnologie',
    'Langlebige Lösung',
    'Umweltfreundlich',
    'Schnelle Amortisation',
  ],
  benefits: [
    'Deutliche Energiekostensenkung',
    'Besseres Raumklima',
    'Geringere Investitionskosten',
    'Langfristige Einsparungen',
    'Wertsteigerung der Immobilie',
    'Umweltschutz',
  ],
  breadcrumbs: [
    { name: 'Home', url: 'https://godeutschland.de' },
    { name: 'Isolierung', url: 'https://godeutschland.de/isolierung' },
  ],
  faqs: [
    {
      question: 'Was ist Low Emission Coating?',
      answer:
        'Low Emission Coating ist eine innovative Beschichtungstechnologie, die Wärmeverluste reduziert und so bis zu 50% der Energiekosten einsparen kann.',
    },
    {
      question: 'Wie viel kostet die Volks-Isolierung?',
      answer:
        'Die Volks-Isolierung kostet ab 90€/m² und ist damit deutlich günstiger als herkömmliche Dämmverfahren.',
    },
    {
      question: 'Wie funktioniert die Low Emission Beschichtung?',
      answer:
        'Die Beschichtung reflektiert Wärmestrahlung zurück in den Raum und reduziert so den Wärmeverlust über die Außenwände erheblich.',
    },
    {
      question: 'Welche Vorteile hat die Volks-Isolierung gegenüber herkömmlicher Dämmung?',
      answer:
        'Sie ist kostengünstiger (bis zu 76% günstiger), verbessert das Raumklima und kann schnell und einfach angebracht werden, ohne die Fassade zu verändern.',
    },
  ],
}

// --- Lazy-loaded components (SSR disabled to optimize LCP/TTI) ---
const IsolierungFAQ = dynamic(() => import('@/components/IsolierungFAQ'), {
  ssr: false,
  loading: () => (
    <div className="flex h-32 w-full items-center justify-center">
      <div className="text-gray-500">FAQ wird geladen...</div>
    </div>
  ),
})

const PriceCalculator = dynamic(() => import('@/components/PriceCalculator'), {
  ssr: false,
  loading: () => (
    <div className="flex h-64 w-full items-center justify-center">
      <div className="text-gray-500">Preisrechner wird geladen...</div>
    </div>
  ),
})

const PriceComparisonTable = dynamic(() => import('@/components/PriceComparisonTable'), {
  ssr: false,
  loading: () => (
    <div className="flex h-48 w-full items-center justify-center">
      <div className="text-gray-500">Preisvergleich wird geladen...</div>
    </div>
  ),
})

const FeatureCards = dynamic(() => import('@/components/FeatureCards'), {
  ssr: false,
  loading: () => (
    <div className="flex h-96 w-full items-center justify-center">
      <div className="text-gray-500">Features werden geladen...</div>
    </div>
  ),
})

const LazyModel = dynamic(() => import('@/components/LazyModel'), {
  ssr: false,
  loading: () => (
    <div className="flex h-48 w-full items-center justify-center rounded bg-gray-100 sm:h-64 dark:bg-gray-800">
      <div className="text-center text-sm text-gray-600 dark:text-gray-400">
        3D-Modell wird geladen …
      </div>
    </div>
  ),
})

const OptimizedYouTubeVideo = dynamic(() => import('@/components/OptimizedYouTubeVideo'), {
  ssr: false,
  loading: () => (
    <div className="flex h-64 w-full items-center justify-center rounded-lg bg-gray-200">
      <div className="text-gray-500">Video wird geladen...</div>
    </div>
  ),
})

export default function IsolierungPage() {
  const scrollToCalculator = () => {
    const calculatorElement = document.getElementById('price-calculator')
    if (calculatorElement) {
      const elementPosition = calculatorElement.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - 200
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
    }
  }

  // Price comparison data
  const comparisonMethods = [
    {
      name: 'Volks-Isolierung (Low Emission Coating)',
      costPerMeter: '90\u00A0€/m²',
      isHighlighted: true,
    },
    {
      name: 'Fassade streichen (inkl. Material & Arbeit)',
      costPerMeter: '91,50 - 112,50\u00A0€/m²',
      savings: 'bis zu 25% günstiger',
    },
    {
      name: 'Wärmedämmverbundsystem, Polystyrol (bis 200 mm)',
      costPerMeter: 'ca. 175\u00A0€/m² + ca. 91,50\u00A0€/m²',
      savings: '176,50\u00A0€/m²',
      percentage: '66%',
    },
    {
      name: 'Wärmedämmverbundsystem, Mineralwolle (bis 200 mm)',
      costPerMeter: 'ca. 195\u00A0€/m² + ca. 91,50\u00A0€/m²',
      savings: '196,50\u00A0€/m²',
      percentage: '69%',
    },
    {
      name: 'Kunststoff-Profilplatten mit Dämmung',
      costPerMeter: 'ca. 195\u00A0€/m² + ca. 91,50\u00A0€/m²',
      savings: '196,50\u00A0€/m²',
      percentage: '69%',
    },
    {
      name: 'Holzbekleidung (hinterlüftete Fassade)',
      costPerMeter: 'ca. 290\u00A0€/m² + ca. 91,50\u00A0€/m²',
      savings: '291,50\u00A0€/m²',
      percentage: '76%',
    },
    {
      name: 'Schieferplatten mit Dämmung',
      costPerMeter: 'ca. 295\u00A0€/m² + ca. 91,50\u00A0€/m²',
      savings: '296,50\u00A0€/m²',
      percentage: '76%',
    },
    {
      name: 'Verblendmauerwerk (Klinker, Dämmung)',
      costPerMeter: 'ca. 350\u00A0€/m² + ca. 91,50\u00A0€/m²',
      savings: '351,50\u00A0€/m²',
      percentage: '79%',
    },
  ]

  return (
    <>
      {/* --- AI-Optimized Schema Markup --- */}
      <AIOptimizedSchema
        type="service"
        serviceData={{
          name: isolierungSchemaData.name,
          description: 'Bis zu 50% weniger Energiekosten durch Low Emission Coating',
          price: isolierungSchemaData.price,
          category: isolierungSchemaData.category,
          features: [
            'Low Emission Coating',
            'Bis zu 50% Energieeinsparung',
            'Verbessertes Raumklima',
          ],
        }}
        breadcrumbs={isolierungSchemaData.breadcrumbs}
        faqs={isolierungSchemaData.faqs}
        url={isolierungSchemaData.url}
      />

      <div>
        {/* --- HERO --- */}
        <div className="relative min-h-screen"></div>
        <div className="absolute inset-0 -z-10">
          <Image
            src="/images/isolierung-hero.webp"
            alt="Gebäudeisolierung"
            quality={85}
            fill
            priority
            fetchPriority="high"
            className="object-cover"
            sizes="100vw"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* Hero Text - LCP Element */}
        <div className="absolute inset-0 z-10">
          <div className="relative z-10 mx-auto max-w-7xl">
            <div className="flex min-h-screen flex-col px-4 pt-32 sm:block sm:px-6 md:px-8 lg:px-10">
              <div className="flex flex-grow flex-col justify-center pb-8 text-center sm:absolute sm:inset-0 sm:mb-60 md:pl-0 md:text-left">
                <div>
                  <h1
                    className="mb-6 text-4xl font-bold text-white md:text-5xl lg:text-6xl"
                    style={{ willChange: 'auto' }}
                  >
                    Die Volks-<span className="text-[#ff4500]">Isolierung</span>
                  </h1>
                  <p className="mb-4 text-xl text-white md:text-2xl">
                    Bis zu 50 % weniger Energiekosten durch Low Emission Coating
                  </p>
                  <p className="mb-8 text-lg text-white" style={{ willChange: 'auto' }}>
                    Diese (Vielen unbekannte) Isolierung reduziert Energiekosten und verbessert das
                    Raumklima zu einem geringen Preis!
                  </p>
                  <div className="mb-8 flex flex-wrap gap-4 md:mb-0">
                    <button
                      onClick={scrollToCalculator}
                      className="inline-block cursor-pointer rounded-lg bg-white px-8 py-4 font-bold text-[#ff4500] transition-all hover:scale-105 hover:bg-gray-100"
                    >
                      Preis berechnen
                    </button>
                    <Link
                      href="/termin"
                      className="inline-block rounded-lg bg-[#ff4500] px-8 py-4 font-bold text-white transition-all hover:scale-105 hover:bg-[#e03e00]"
                    >
                      Jetzt Angebot anfordern
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- BENTO GRID (Model + Video + Highlights) --- */}
        <div className="relative z-10 bg-white dark:bg-gray-900">
          <FullBleedSection className="bg-white py-8 sm:py-12 dark:bg-gray-900">
            <div className="mx-auto max-w-7xl">
              {/* Row 1 */}
              <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-12">
                {/* 50% Box */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="col-span-1 flex flex-col items-center justify-center rounded-md bg-[#ff4500] p-6 text-center text-white sm:col-span-3 sm:p-8"
                >
                  <div className="py-4 text-center">
                    <h2 className="mb-2 text-4xl font-bold sm:text-6xl">
                      <span>Bis</span> <span>50</span>
                      <span>%</span>
                    </h2>
                    <p className="text-sm sm:text-base">weniger Energiekosten</p>
                  </div>
                </motion.div>

                {/* Raumklima + 3D Model */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="col-span-1 flex flex-col rounded-md bg-[#e9e9e6] p-4 text-[#21262b] sm:col-span-6 sm:p-6"
                >
                  <div className="grid grid-cols-1 gap-4 text-center sm:grid-cols-2 md:text-left">
                    <div>
                      <h2 className="mt-2 text-2xl font-bold sm:text-4xl">
                        <span>Verbessertes </span>
                      </h2>
                      <h2 className="mt-2 text-2xl font-bold sm:text-4xl">
                        <span>Raumklima</span>
                      </h2>
                      <p className="mt-4 text-sm sm:text-base">
                        Low Emission Coating ist isolierend, aber diffusionsoffen. Schimmel,
                        Feinstaub und Geruchsbildung wird entgegengewirkt. Anwender berichten von
                        einem Wohlfühl-Raumklima nach Anbringung.
                      </p>
                    </div>
                    <div className="mt-4 flex items-center justify-center sm:mt-0 sm:justify-end">
                      <div className="relative h-48 w-full sm:h-64">
                        <LazyModel
                          modelPath="/models/isoryx-model.glb"
                          className="absolute inset-0 h-full w-full"
                          options={{
                            scale: 8,
                            position: [0, 0.2, 0],
                            rotation: [0, Math.PI / 4, 0],
                            intensity: 1.5,
                            roughness: 0.4,
                            metalness: 0.2,
                            envMapIntensity: 0.6,
                          }}
                          rootMargin="200px"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Garantie Box */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  className="col-span-1 flex flex-col items-center justify-center rounded-md bg-[#e9e9e6] p-6 text-center text-[#21262b] sm:col-span-3 sm:p-8"
                >
                  <h2 className="text-3xl font-bold sm:text-5xl">
                    <span>10</span> <span className="text-xl sm:text-2xl">Jahre Garantie!</span>
                  </h2>
                </motion.div>
              </div>

              {/* Row 2 */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
                {/* Left column */}
                <div className="col-span-1 md:col-span-6">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="mb-4 rounded-md bg-white"
                  >
                    <div className="p-6 text-center sm:p-6">
                      <h2 className="text-2xl font-bold sm:text-4xl">
                        <span>Ökologisch, mineralisch</span> <span> und nachhaltig</span>
                      </h2>
                    </div>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    className="rounded-md"
                    style={{ zIndex: 101 }}
                  >
                    <LazyBackgroundVideo
                      src="/videos/isolation.webm"
                      className="rounded-md p-6 sm:p-3"
                      fallbackColor="bg-black"
                    >
                      <div className="relative z-10 flex h-full items-center justify-center py-4">
                        <div className="text-center">
                          <h2 className="text-2xl font-bold text-white sm:text-4xl">
                            Mit der Natur
                          </h2>
                          <p className="text-base text-white sm:text-xl">als Vorbild</p>
                        </div>
                      </div>
                    </LazyBackgroundVideo>
                  </motion.div>
                </div>

                {/* Right column */}
                <div className="col-span-1 md:col-span-6">
                  <div className="grid h-full grid-cols-1 gap-4 sm:grid-cols-2">
                    {/* Kostengünstig */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.5 }}
                      className="flex flex-col items-center justify-center rounded-md bg-[#ff4500] text-center text-white"
                    >
                      <div className="w-full overflow-hidden p-2 sm:py-16">
                        <h2 className="mb-2 text-3xl font-bold break-words sm:text-5xl">
                          Kosten-
                          <br />
                          günstig
                        </h2>
                        <p className="text-base break-words sm:text-lg">in Erwerb und Anbringung</p>
                      </div>
                    </motion.div>

                    {/* Einfache Anbringung (image bg) */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.6 }}
                      className="relative flex flex-col items-center justify-center overflow-hidden rounded-md text-white"
                    >
                      <div className="absolute inset-0 z-10 bg-[#21262b] opacity-50" />
                      <div className="absolute inset-0 z-0">
                        <Image
                          src="/images/final-Schicht.webp"
                          alt="Witterungsschutz für Fassaden"
                          fill
                          className="object-cover"
                          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 25vw, 20vw"
                          loading="lazy"
                        />
                      </div>
                      <div className="relative z-20 px-4 py-8 text-center sm:px-8">
                        <h2 className="mb-2 text-2xl font-bold sm:text-4xl">
                          Einfache
                          <br />
                          Anbringung
                        </h2>
                        <p className="text-base sm:text-lg">
                          Wird einfach als Endanstrich an die Wand aufgetragen
                        </p>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </FullBleedSection>

          {/* --- FEATURE CARDS --- */}
          <FullBleedSection className="bg-white py-10 dark:bg-gray-900">
            <FeatureCards
              title="Vorteile unserer"
              titleHighlight="Volks-Isolierung"
              features={[
                {
                  image: '/images/isolierung1.webp',
                  imageAlt: 'Energieeffizienz durch Isolation',
                  title: 'Spart Energiekosten',
                  description:
                    'Unsere Isolierung reduziert den Energieverbrauch um bis zu 50% und sorgt für erhebliche Kosteneinsparungen bei Ihren Heizkosten.',
                  icon: (
                    <svg
                      className="h-full w-full text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  ),
                },
                {
                  image: '/images/isolierung2.webp',
                  imageAlt: 'Kosteneinsparung durch Isolierung',
                  title: 'Kosteneinsparung',
                  description:
                    'Mit unserer Isolierung sparen Sie nicht nur Heizkosten. Sie sparen auch die Kosten für eine teure Isolierung und die Kosten für einen Anstrich!',
                  icon: (
                    <svg
                      className="h-full w-full text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  ),
                },
                {
                  image: '/images/isolierung3.webp',
                  imageAlt: 'Erhöhter Wohnkomfort durch Isolierung',
                  title: 'Wohlfühl-Raumklima',
                  description:
                    'Low Emission Coating führt zu einem angenehmen Raumklima. Die Isolierung beugt Schimmel und Gerüchen vor. Zudem mindert sie die Feinstaub-Belastung.',
                  icon: (
                    <svg
                      className="h-full w-full text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                      />
                    </svg>
                  ),
                },
              ]}
            />
          </FullBleedSection>

          {/* --- CONTENT + YOUTUBE --- */}
          <FullBleedSection className="bg-gray-50 py-20 dark:bg-gray-800">
            <div className="mx-auto max-w-7xl">
              <div className="grid gap-8 md:grid-cols-2">
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <h2 className="mb-6 text-center text-3xl font-bold md:text-left md:text-4xl">
                    Volks-Isolierung <span className="text-[#ff4500]">schlägt Isolierung</span>
                  </h2>
                  <p className="mb-8 text-center text-lg text-gray-600 md:text-left dark:text-gray-300">
                    Auch wenn eine Isolierung in einzelnen Fällen einen positiven Effekt hat, muss
                    das nicht die Regel sein.
                  </p>
                  <p className="mb-8 text-center text-lg text-gray-600 md:text-left dark:text-gray-300">
                    Bereits 2012 berichtete WELT: "Mehrere Studien belegen einen höheren
                    Energieverbrauch bei gedämmten Wohnhäusern."
                  </p>
                  <p className="mb-8 text-center text-lg text-gray-600 md:text-left dark:text-gray-300">
                    Wir wissen aus Erfahrung mittels Projekten weltweit, dass sich mit Low Emission
                    Coating wesentlich mehr Energie einsparen lässt und das bei viel weniger Kosten
                    für die Anbringung.
                  </p>
                  <ul className="mb-8 space-y-4">
                    <li className="flex items-center text-gray-600 dark:text-gray-300">
                      <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                      Einfache und schnelle Anbringung
                    </li>
                    <li className="flex items-center text-gray-600 dark:text-gray-300">
                      <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                      Bis zu 50% weniger Energiekosten
                    </li>
                    <li className="flex items-center text-gray-600 dark:text-gray-300">
                      <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                      Schützt vor Witterungseinflüssen
                    </li>
                    <li className="flex items-center text-gray-600 dark:text-gray-300">
                      <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                      Verhindert Schimmelbildung
                    </li>
                    <li className="flex items-center text-gray-600 dark:text-gray-300">
                      <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                      Verbessertes Raumklima
                    </li>
                    <li className="flex items-center text-gray-600 dark:text-gray-300">
                      <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                      Wesentlich kostengünstiger als Isolierung
                    </li>
                    <li className="flex items-center text-gray-600 dark:text-gray-300">
                      <span className="mr-2 h-5 w-5 text-[#ff4500]">✓</span>
                      Ideal für Bestandsgebäude und Neubau
                    </li>
                  </ul>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                  className="relative min-h-[400px]"
                >
                  <OptimizedYouTubeVideo videoId="Z9oLUrWay9k" title="Low Emission Coating Video" />
                </motion.div>
              </div>
            </div>
          </FullBleedSection>

          {/* --- CTA --- */}
          <FullBleedSection className="bg-white py-30">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mx-auto max-w-7xl px-4 text-center"
            >
              <h2 className="mb-6 text-3xl font-bold text-black md:text-4xl">
                Bereit für eine effiziente Isolierung?
              </h2>
              <p className="mx-auto mb-8 max-w-3xl text-lg text-gray-700">
                Kontaktieren Sie uns noch heute für eine kostenlose Beratung und ein
                maßgeschneidertes Angebot für Ihr Haus. Beginnen Sie jetzt mit dem Sparen von
                Energiekosten und verbessern Sie Ihren Wohnkomfort.
              </p>
              <Link
                href="/termin"
                className="inline-block rounded-lg bg-[#ff4500] px-8 py-4 font-bold text-white transition-transform hover:scale-105 hover:bg-[#e03e00]"
              >
                Kostenlose Beratung vereinbaren
              </Link>
            </motion.div>
          </FullBleedSection>

          {/* --- PRICE COMPARISON --- */}
          <FullBleedSection className="bg-gray-50 py-16 dark:bg-gray-800">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mx-auto max-w-7xl px-4"
            >
              <span className="mb-6 block text-center text-3xl font-bold sm:text-4xl md:text-4xl">
                <span className="text-[#ff4500]">2-in-1</span> Lösung
              </span>
              <h2 className="mb-6 text-center text-3xl font-bold md:text-4xl">
                Deutlich <span className="text-[#ff4500]">kostengünstiger</span> als herkömmliche
                Fassadenisolierung
              </h2>
              <p className="mx-auto mb-8 max-w-3xl text-center text-lg text-gray-700 dark:text-gray-300">
                Vergleichen Sie selbst: Unsere Low Emission Coating Technologie ist eine 2-in-1
                Lösung, die sowohl isoliert als auch die Fassade beschichtet. Traditionelle Methoden
                erfordern hingegen zwei separate Kosten: die Isolierung plus das Streichen der
                Fassade.
              </p>

              <PriceComparisonTable methods={comparisonMethods} />

              <div className="mt-8 text-center">
                <p className="mx-auto max-w-2xl text-base text-gray-600 dark:text-gray-400">
                  * Alle Kosten sind Richtwerte und dienen zur Orientierung. Die tatsächlichen
                  Kosten können je nach individueller Situation variieren.
                </p>
                <p className="mx-auto mt-4 max-w-2xl text-base font-medium text-gray-700 dark:text-gray-300">
                  Volks-Isolierung bietet eine entscheidende Kostenersparnis durch ihre 2-in-1
                  Lösung: Isolierung und Fassadenbeschichtung in einem Arbeitsgang, während
                  traditionelle Methoden zwei separate Arbeitsgänge und Materialien erfordern.
                </p>
              </div>
            </motion.div>
          </FullBleedSection>

          {/* --- PRICE CALCULATOR --- */}
          <FullBleedSection className="bg-white py-24 dark:bg-gray-900">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mx-auto max-w-7xl"
              id="price-calculator"
            >
              <PriceCalculator />
            </motion.div>
          </FullBleedSection>

          {/* --- FAQ --- */}
          <IsolierungFAQ />
        </div>
      </div>
    </>
  )
}
